import { EntryScore } from "./lineup";
import { PlayerPick } from "../models";

/**
 * Win probability and projected score for a head-to-head matchup.
 *
 * Each remaining player's points are modelled as a normal distribution centred on
 * FPL's expected points for the round (scaled by the fraction of their games still to
 * play), with a spread that grows with the expectation. The difference between the two
 * sides is then also normal, so the probabilities come from a closed-form CDF rather
 * than a simulation. Players picked by both sides cancel out, so shared players never
 * move the odds. It is a heuristic and is labelled as such in the UI.
 */
export type MatchupPrediction = {
	/** Integer percentages summing to 100. */
	home: number;
	draw: number;
	away: number;
	projectedHome: number;
	projectedAway: number;
	/** True when nothing remains to be played on either side. */
	settled: boolean;
};

/** Spread of a player's points around their expectation, in points. */
export const pointsSpread = (expected: number) => 1.6 + 0.5 * expected;

/** Fraction of a player's round still to be played, from 0 to 1. */
export const fractionRemaining = (player: PlayerPick): number => {
	if (player.games.length === 0) {
		return 0;
	}
	const remaining = player.games.reduce((sum, g) => sum + (g.finished ? 0 : g.started ? Math.max(0, 90 - g.minutes) / 90 : 1), 0);
	return remaining / player.games.length;
};

/** Points multiplier for a player in this side's lineup. */
const multiplierFor = (player: PlayerPick, chip: string | null) => {
	if (player.active !== 1) {
		return 0;
	}
	if (player.captain) {
		return chip === "3xc" ? 3 : 2;
	}
	return 1;
};

type Contribution = { mean: number; variance: number };

const remainingContribution = (player: PlayerPick, multiplier: number): Contribution => {
	const fraction = fractionRemaining(player);
	if (fraction === 0 || multiplier === 0) {
		return { mean: 0, variance: 0 };
	}
	const expected = player.expectedPoints * fraction;
	const spread = pointsSpread(player.expectedPoints) * Math.sqrt(fraction);
	return { mean: expected * multiplier, variance: (spread * multiplier) ** 2 };
};

/** Standard normal cumulative distribution, via the Abramowitz–Stegun erf approximation. */
export const normalCdf = (z: number): number => {
	const t = 1 / (1 + 0.2316419 * Math.abs(z));
	const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
	const tail = Math.exp((-z * z) / 2) / Math.sqrt(2 * Math.PI) * poly;
	return z >= 0 ? 1 - tail : tail;
};

/** Rounds three probabilities to integer percentages that sum to exactly 100. */
export const toPercentages = (home: number, draw: number, away: number): [number, number, number] => {
	const raw = [home, draw, away].map((p) => p * 100);
	const floors = raw.map(Math.floor);
	let remainder = 100 - floors.reduce((a, b) => a + b, 0);
	const order = raw.map((v, i) => ({ frac: v - floors[i], i })).sort((a, b) => b.frac - a.frac);
	for (const { i } of order) {
		if (remainder <= 0) break;
		floors[i]++;
		remainder--;
	}
	return [floors[0], floors[1], floors[2]];
};

export const predictMatchup = (home: EntryScore, away: EntryScore): MatchupPrediction => {
	const homeMult = new Map(home.players.map((p) => [p.id, multiplierFor(p, home.chipCode)]));
	const awayMult = new Map(away.players.map((p) => [p.id, multiplierFor(p, away.chipCode)]));
	const byId = new Map<number, PlayerPick>();
	home.players.forEach((p) => byId.set(p.id, p));
	away.players.forEach((p) => byId.set(p.id, p));

	let homeMean = home.totalPoints;
	let awayMean = away.totalPoints;
	let diffVariance = 0;

	byId.forEach((player, id) => {
		const hm = homeMult.get(id) ?? 0;
		const am = awayMult.get(id) ?? 0;
		homeMean += remainingContribution(player, hm).mean;
		awayMean += remainingContribution(player, am).mean;
		// shared players only add the variance of the difference in multipliers
		diffVariance += remainingContribution(player, Math.abs(hm - am)).variance;
	});

	const diffMean = homeMean - awayMean;
	const settled = diffVariance === 0;

	let pHome: number;
	let pDraw: number;
	let pAway: number;

	if (settled) {
		pHome = diffMean > 0 ? 1 : 0;
		pAway = diffMean < 0 ? 1 : 0;
		pDraw = diffMean === 0 ? 1 : 0;
	} else {
		const sd = Math.sqrt(diffVariance);
		// points are whole numbers, so a draw is a difference inside half a point
		pHome = 1 - normalCdf((0.5 - diffMean) / sd);
		pAway = normalCdf((-0.5 - diffMean) / sd);
		pDraw = Math.max(0, 1 - pHome - pAway);
	}

	const [homePct, drawPct, awayPct] = toPercentages(pHome, pDraw, pAway);

	return {
		home: homePct,
		draw: drawPct,
		away: awayPct,
		projectedHome: Math.round(homeMean),
		projectedAway: Math.round(awayMean),
		settled,
	};
};
